import { compact, isEmpty } from 'lodash/fp';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { ApplicationState, CommanderThunkDispatch, WithBillId, WithGuideLink } from '@mews-commander/types';
import { ModalBodyWrapper, ModalWrapper } from '@mews-commander/ui';
import { createAction, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import {
    DEFAULT_BILL_FETCH_EXTENT,
    fetchCurrentEnterpriseBillsByIds,
} from '@mews-commander/legacy-entities/src/actions/bills';
import { fetchServiceOrderNotesByProductServiceOrderIds } from '@mews-commander/legacy-entities/src/actions/productServiceOrders';
import { getBill, getBillOwnerId } from '@mews-commander/legacy-entities/src/ducks/bills';
import { fetchAccountById } from '@mews-commander/legacy-entities/src/actions/accounts';
import { fetchAllEnterpriseServices } from '@mews-commander/legacy-entities/src/actions/services';
import { fetchCurrentEnterpriseReservationsByReservationIds } from '@mews-commander/legacy-entities/src/actions/reservations';
import { fetchRebateReasons } from '@mews-commander/legacy-entities/src/actions/rebateReasons';
import { canCurrentProfileRebateItems } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { getProductServiceOrderIds } from '../selectors';
import { getReservationIdsByBillIds } from '../ducks/reservations';
import { useCloseButton } from '../../../core/windowManager/hooks';
import { changeDocumentTitleLocalized } from '../../../middleware/createDocumentTitleMiddleware';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { createRebateBillScope } from './utils';
import { REBATE_BILL_EXTENT } from './constants';
import RebateScreen from './components/RebateScreen';
import { REBATE_FORM_INIT } from './actions/actionTypes';

type RebateBillContainerProps = WithGuideLink & WithBillId;

export const prefetchRebateForm = async (
    dispatch: CommanderThunkDispatch,
    getState: () => ApplicationState,
    billId: string
) => {
    const scope = createRebateBillScope(billId);

    const promise = dispatch(
        fetchCurrentEnterpriseBillsByIds({
            billIds: [billId],
            extent: { ...DEFAULT_BILL_FETCH_EXTENT, ...REBATE_BILL_EXTENT },
        })
    ).then(async () => {
        const state = getState();
        const enterpriseId = getCurrentEnterpriseId();
        const accountId = getBillOwnerId(state, { id: billId });
        const productServiceOrderIds = getProductServiceOrderIds(state, { billIds: [billId] });
        let serviceOrderNotesPromise;
        let enterpriseReservationsPromise;

        if (!isEmpty(productServiceOrderIds)) {
            serviceOrderNotesPromise = dispatch(
                fetchServiceOrderNotesByProductServiceOrderIds({ enterpriseId, productServiceOrderIds })
            );
        }

        // @ts-expect-error
        const accountPromise = dispatch(fetchAccountById({ accountId }));
        const enterpriseServicesPromise = dispatch(fetchAllEnterpriseServices({ enterpriseIds: [enterpriseId] }));

        const reservationIds = getReservationIdsByBillIds(state, { billIds: [billId] });

        if (!isEmpty(reservationIds)) {
            enterpriseReservationsPromise = dispatch(
                fetchCurrentEnterpriseReservationsByReservationIds({ reservationIds, extent: { accounts: true } })
            );
        }

        const rebateReasonsPromise = dispatch(fetchRebateReasons());

        const promises = compact([
            serviceOrderNotesPromise,
            accountPromise,
            enterpriseServicesPromise,
            enterpriseReservationsPromise,
            rebateReasonsPromise,
        ]);

        return Promise.all(promises);
    });

    return dispatch(createAction(REBATE_FORM_INIT, promise, { scope }));
};

export const checkRebateFormPrivilege = (state: ApplicationState, billId: string) => {
    const canRebate: boolean = canCurrentProfileRebateItems(state);
    const bill = getBill(state, { id: billId });

    return canRebate === true && bill != null;
};

export const RebateBillContainer: React.FC<RebateBillContainerProps> = ({ billId, guideLink }) => {
    const dispatch: CommanderThunkDispatch = useDispatch();
    const bill = useCommanderSelector((state) => getBill(state, { id: billId }));
    const closeButton = useCloseButton();

    useEffect(() => {
        if (bill?.name != null) {
            dispatch(changeDocumentTitleLocalized(TextKey.RebateBill, { billName: bill.name }));
        }
    }, [dispatch, bill]);

    if (!bill) {
        return null;
    }

    const scope = createRebateBillScope(bill.id);

    return (
        <ModalWrapper>
            <ModalHeader
                title={
                    bill?.name ? (
                        <LocalizedText textKey={TextKey.RebateBill} substitutes={{ billName: bill.name }} />
                    ) : (
                        <LocalizedText textKey={TextKey.RebateBillSingle} />
                    )
                }
                primaryAction={closeButton}
            />
            <ModalBodyWrapper padded>
                <RebateScreen billId={bill.id} scope={scope} guideLink={guideLink} accountId={bill.ownerId} />
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};
